import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  HoverCard,
  LoadingOverlay,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  createOrgUser,
  deleteOrgUser,
  getUsersLicenseInfo,
  getUsersOfOrg,
  reissueOfflineLicense,
  renameOrgUser,
  RenameOrgUserResponse,
  resetUserPassword,
} from "@utils/api-communication/RestApiCli";
import {
  IconCheck,
  IconDeviceDesktop,
  IconKey,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { MODAL_POPOVER_Z_INDEX, MODAL_Z_INDEX } from "@constants/zIndex";
import { Role } from "@model/role";
import dayjs from "dayjs";
import {
  DEFAULT_LICENSE_EXPIRY_SELECTION,
  expiryIsoFromSelection,
  getUsername,
  isLicenseExpirySelectionIncomplete,
  LicenseExpirySelection,
} from "@utils/CommonUtil";
import LicenseExpirySelect from "@components/common/LicenseExpirySelect";
import { selectUserRole } from "@utils/redux-utils/selectors/userSelectors";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  apiErrorMessage,
  canManageOrgUsers,
  CREATABLE_ORG_USER_ROLES,
  CreatableOrgUserRole,
  renameError,
  temporaryPasswordError,
  totalMigratedRecords,
  usernameError,
} from "./orgUserManagement";

type LicenseStatus = "active" | "pending" | "expired" | "none";

interface UserLicenseInfo {
  username: string;
  hasLicense: boolean;
  requestCount: number;
  licenseGenerationDate: string | null;
  licenseExpiresAt: string | null;
  lastSyncDate: string | null;
  lastRequestDate: string | null;
  status: LicenseStatus;
}

interface UserListProps {
  orgId?: string | null;
  usernameToExclude?: string;
  onOrgIdChanged?: (newOrgId: string) => void;
}

interface CreateUserFormValues {
  username: string;
  password: string;
  role: CreatableOrgUserRole;
  name: string;
  email: string;
}

interface RenameUserFormValues {
  username: string;
  migrateOrganization: boolean;
}

const LICENSE_ICON_COLOR: Record<Exclude<LicenseStatus, "none">, string> = {
  active: "blue",
  pending: "orange",
  expired: "red",
};

const EMPTY_CREATE_FORM: CreateUserFormValues = {
  username: "",
  password: "",
  role: Role.User,
  name: "",
  email: "",
};

function formatDate(date: string | null): string {
  return date ? dayjs(date).format("YYYY-MM-DD") : "—";
}

// A proxy answering 200 with an error body or an HTML page would otherwise crash the
// whole settings screen on `.forEach` / `.filter`.
export function toLicenceInfoMap(data: unknown): Record<string, UserLicenseInfo> {
  const map: Record<string, UserLicenseInfo> = {};
  if (!Array.isArray(data)) {
    return map;
  }
  for (const info of data as UserLicenseInfo[]) {
    if (info?.username) {
      map[info.username] = info;
    }
  }
  return map;
}

// The server decides `expired` when it answers, but the page can sit open across the
// moment a licence lapses, so the date is what the row is coloured and labelled from.
export function licenceDisplayStatus(
  info: Pick<UserLicenseInfo, "hasLicense" | "licenseExpiresAt" | "status">,
  now: number = Date.now(),
): LicenseStatus {
  if (!info.hasLicense || !info.licenseExpiresAt) {
    return info.status;
  }
  return dayjs(info.licenseExpiresAt).valueOf() <= now ? "expired" : info.status;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function UserList({ orgId, usernameToExclude, onOrgIdChanged }: UserListProps) {
  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const [licenseInfoMap, setLicenseInfoMap] = useState<Record<string, UserLicenseInfo>>({});
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resettingUsername, setResettingUsername] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reissueTarget, setReissueTarget] = useState<User | null>(null);
  const [reissueExpiresAt, setReissueExpiresAt] = useState<LicenseExpirySelection>(
    DEFAULT_LICENSE_EXPIRY_SELECTION,
  );
  const [reissueError, setReissueError] = useState("");
  const [reissuingUsername, setReissuingUsername] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [renameTarget, setRenameTarget] = useState<User | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameFailure, setRenameFailure] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { t } = useTranslation();
  const userRole = useSelector(selectUserRole);
  const currentUsername = getUsername();
  const canManageUsers = canManageOrgUsers(userRole);
  const canResetPasswords = canManageUsers || userRole === Role.Distributor;
  const canReissueLicenses = canResetPasswords || userRole === Role.Manager;
  const showActions = canResetPasswords || canReissueLicenses;
  const isResetting = !!resettingUsername;
  const isReissuing = !!reissuingUsername;

  const errorLabel = (key: string | null) =>
    key ? t(`UserSettings.userForm.errors.${key}`) : null;

  const createForm = useForm<CreateUserFormValues>({
    initialValues: EMPTY_CREATE_FORM,
    validate: {
      username: (value) => errorLabel(usernameError(value)),
      password: (value) => errorLabel(temporaryPasswordError(value)),
      email: (value) => (value.trim() && !isValidEmail(value) ? t("invalidEmail") : null),
    },
  });

  const renameForm = useForm<RenameUserFormValues>({
    initialValues: { username: "", migrateOrganization: false },
    validate: {
      username: (value) => errorLabel(renameError(value, renameTarget?.username ?? "")),
    },
  });

  const refresh = () => setRefreshKey((key) => key + 1);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let usersLoaded = false;
    let licenseLoaded = false;

    const checkDone = () => {
      if (usersLoaded && licenseLoaded) setLoading(false);
    };

    setLoading(true);

    getUsersOfOrg(
      { orgId },
      ({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) {
          console.error("[UserList] Expected an array of org users, got:", data);
        }
        setUsers(Array.isArray(data) ? data : []);
        usersLoaded = true;
        checkDone();
      },
      (error) => {
        if (cancelled) return;
        console.error("[UserList] Failed to fetch org users:", error);
        usersLoaded = true;
        checkDone();
      },
    );

    getUsersLicenseInfo(
      orgId,
      ({ data }: { data: UserLicenseInfo[] }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) {
          console.error("[UserList] Expected an array of license info, got:", data);
        }
        setLicenseInfoMap(toLicenceInfoMap(data));
        licenseLoaded = true;
        checkDone();
      },
      (error) => {
        if (cancelled) return;
        console.error("[UserList] Failed to fetch license info:", error);
        licenseLoaded = true;
        checkDone();
      },
    );

    return () => {
      cancelled = true;
    };
  }, [orgId, refreshKey]);

  const users_ = useMemo(
    () =>
      Array.isArray(users)
        ? users.filter(({ username }) => username !== usernameToExclude)
        : undefined,
    [users, usernameToExclude],
  );

  const roleOptions = CREATABLE_ORG_USER_ROLES.map((role) => ({
    value: role,
    label: t(`UserSettings.roles.${role}`),
  }));

  const roleLabel = (role: string | undefined) =>
    role ? t(`UserSettings.roles.${role}`, role) : "—";

  const openResetModal = (user: User) => {
    setResetTarget(user);
    setResetEmail(user.email ?? "");
    setResetError("");
    setSuccessMessage(null);
  };

  const closeResetModal = () => {
    if (isResetting) return;
    setResetTarget(null);
    setResetEmail("");
    setResetError("");
  };

  const openReissueModal = (user: User) => {
    setReissueTarget(user);
    setReissueExpiresAt(DEFAULT_LICENSE_EXPIRY_SELECTION);
    setReissueError("");
    setSuccessMessage(null);
  };

  const closeReissueModal = () => {
    if (isReissuing) return;
    setReissueTarget(null);
    setReissueExpiresAt(DEFAULT_LICENSE_EXPIRY_SELECTION);
    setReissueError("");
  };

  const openCreateModal = () => {
    createForm.setValues(EMPTY_CREATE_FORM);
    createForm.resetDirty(EMPTY_CREATE_FORM);
    createForm.clearErrors();
    setCreateError("");
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const openRenameModal = (user: User) => {
    setRenameTarget(user);
    renameForm.setValues({ username: user.username, migrateOrganization: false });
    renameForm.clearErrors();
    setRenameFailure("");
  };

  const closeRenameModal = () => {
    if (renaming) return;
    setRenameTarget(null);
    setRenameFailure("");
  };

  const handleReissueLicense = () => {
    if (!reissueTarget) return;

    setReissueError("");
    setReissuingUsername(reissueTarget.username);
    const expiresAt = expiryIsoFromSelection(reissueExpiresAt);
    reissueOfflineLicense(
      {
        username: reissueTarget.username,
        ...(expiresAt ? { expiresAt } : {}),
      },
      (response) => {
        const emails: string[] = response?.data?.emails ?? [];
        setSuccessMessage(
          t("UserSettings.reissueLicenseEmailSent", {
            username: reissueTarget.username,
            emails: emails.join(", "),
          }),
        );
        setReissueTarget(null);
        setReissueExpiresAt(DEFAULT_LICENSE_EXPIRY_SELECTION);
        setReissuingUsername(null);
        refresh();
      },
      (error) => {
        console.error("[UserList] Failed to reissue offline license:", error);
        setReissueError(apiErrorMessage(error, t("UserSettings.reissueLicenseError")));
        setReissuingUsername(null);
      },
    );
  };

  const handleResetPassword = () => {
    if (!orgId || !resetTarget) return;

    const email = resetEmail.trim();
    if (!isValidEmail(email)) {
      setResetError(t("UserSettings.resetPasswordInvalidEmail"));
      return;
    }

    setResetError("");
    setResettingUsername(resetTarget.username);
    resetUserPassword(
      { username: resetTarget.username, orgId, email },
      (response) => {
        const destination = response?.data?.email || email;
        setSuccessMessage(t("UserSettings.resetPasswordEmailSent", { email: destination }));
        setResetTarget(null);
        setResetEmail("");
        setResettingUsername(null);
      },
      (error) => {
        console.error("[UserList] Failed to reset user password:", error);
        setResetError(apiErrorMessage(error, t("UserSettings.resetPasswordError")));
        setResettingUsername(null);
      },
    );
  };

  const handleCreateUser = (values: CreateUserFormValues) => {
    if (!orgId) return;

    const username = values.username.trim();
    setCreateError("");
    setCreating(true);
    createOrgUser(
      {
        orgId,
        username,
        password: values.password,
        role: values.role,
        name: values.name.trim() || undefined,
        email: values.email.trim() || undefined,
      },
      () => {
        setCreating(false);
        setCreateOpen(false);
        notifications.show({
          color: "green",
          message: t("UserSettings.userForm.created", { username }),
        });
        refresh();
      },
      (error) => {
        console.error("[UserList] Failed to create org user:", error);
        setCreateError(apiErrorMessage(error, t("UserSettings.userForm.error")));
        setCreating(false);
      },
    );
  };

  const performRename = (target: User, values: RenameUserFormValues) => {
    const username = values.username.trim();
    const migrateOrganization = values.migrateOrganization;
    setRenameFailure("");
    setRenaming(true);
    renameOrgUser(
      target._id,
      { username, migrateOrganization },
      ({ data }: { data: RenameOrgUserResponse }) => {
        setRenaming(false);
        setRenameTarget(null);
        if (data?.migration) {
          notifications.show({
            color: "green",
            autoClose: 10000,
            message: t("UserSettings.rename.migrated", {
              username,
              oldOrgId: data.migration.oldOrgId,
              newOrgId: data.migration.newOrgId,
              count: totalMigratedRecords(data.migration),
            }),
          });
          if (onOrgIdChanged) {
            onOrgIdChanged(data.migration.newOrgId);
            return;
          }
        } else {
          notifications.show({
            color: "green",
            message: t("UserSettings.rename.renamed", {
              previousUsername: target.username,
              username,
            }),
          });
        }
        refresh();
      },
      (error) => {
        console.error("[UserList] Failed to rename org user:", error);
        const detail = apiErrorMessage(error, t("UserSettings.rename.error"));
        setRenameFailure(
          migrateOrganization ? `${t("UserSettings.rename.migrateFailed")} ${detail}` : detail,
        );
        setRenaming(false);
      },
    );
  };

  const handleRenameSubmit = (values: RenameUserFormValues) => {
    if (!renameTarget) return;
    if (!values.migrateOrganization) {
      performRename(renameTarget, values);
      return;
    }
    const target = renameTarget;
    modals.openConfirmModal({
      title: t("UserSettings.rename.migrateConfirmTitle"),
      zIndex: MODAL_POPOVER_Z_INDEX,
      centered: true,
      children: (
        <Text size="sm">
          {t("UserSettings.rename.migrateConfirmMessage", {
            orgId: target.orgId || orgId,
            newOrgId: values.username.trim(),
          })}
        </Text>
      ),
      labels: {
        confirm: t("UserSettings.rename.migrateConfirmButton"),
        cancel: t("UserSettings.cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => performRename(target, values),
    });
  };

  const handleDelete = (user: User) => {
    setDeletingId(user._id);
    deleteOrgUser(
      user._id,
      () => {
        setDeletingId(null);
        notifications.show({
          color: "green",
          message: t("UserSettings.deleteUserDone", { username: user.username }),
        });
        refresh();
      },
      (error) => {
        console.error("[UserList] Failed to delete org user:", error);
        setDeletingId(null);
        notifications.show({
          color: "red",
          message: apiErrorMessage(error, t("UserSettings.deleteUserError")),
        });
      },
    );
  };

  const confirmDelete = (user: User) => {
    modals.openConfirmModal({
      title: t("UserSettings.deleteUser"),
      zIndex: MODAL_Z_INDEX,
      centered: true,
      children: (
        <Text size="sm">{t("UserSettings.deleteUserConfirm", { username: user.username })}</Text>
      ),
      labels: {
        confirm: t("UserSettings.deleteUserConfirmButton"),
        cancel: t("UserSettings.cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => handleDelete(user),
    });
  };

  const renameTargetOrgId = renameTarget?.orgId || orgId || "";
  const columnCount = 7 + (showActions ? 1 : 0);

  return (
    <Card shadow="sm" p="lg" withBorder>
      <Modal
        opened={!!resetTarget}
        onClose={closeResetModal}
        title={t("UserSettings.resetPassword")}
        zIndex={MODAL_Z_INDEX}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {t("UserSettings.resetPasswordConfirm", {
              username: resetTarget?.username ?? "",
            })}
          </Text>
          <TextInput
            type="email"
            label={t("UserSettings.resetPasswordEmail")}
            value={resetEmail}
            onChange={(event) => setResetEmail(event.currentTarget.value)}
            disabled={isResetting}
            error={resetEmail.trim() && !isValidEmail(resetEmail) ? t("invalidEmail") : undefined}
            withAsterisk
          />
          {resetError && (
            <Alert color="red" variant="light">
              {resetError}
            </Alert>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="outline" onClick={closeResetModal} disabled={isResetting}>
              {t("UserSettings.cancel")}
            </Button>
            <Button
              onClick={handleResetPassword}
              loading={isResetting}
              disabled={!isValidEmail(resetEmail)}
            >
              {t("UserSettings.resetPasswordSendEmail")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!reissueTarget}
        onClose={closeReissueModal}
        title={t("UserSettings.reissueLicense")}
        zIndex={MODAL_Z_INDEX}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {t("UserSettings.reissueLicenseConfirm", {
              username: reissueTarget?.username ?? "",
            })}
          </Text>
          <LicenseExpirySelect
            disabled={isReissuing}
            value={reissueExpiresAt}
            onChange={setReissueExpiresAt}
            dropdownZIndex={MODAL_POPOVER_Z_INDEX}
          />
          {reissueError && (
            <Alert color="red" variant="light">
              {reissueError}
            </Alert>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="outline" onClick={closeReissueModal} disabled={isReissuing}>
              {t("UserSettings.cancel")}
            </Button>
            <Button
              onClick={handleReissueLicense}
              loading={isReissuing}
              disabled={isLicenseExpirySelectionIncomplete(reissueExpiresAt)}
            >
              {t("UserSettings.reissueLicenseSubmit")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={createOpen}
        onClose={closeCreateModal}
        title={t("UserSettings.userForm.title")}
        zIndex={MODAL_Z_INDEX}
        centered
      >
        <form onSubmit={createForm.onSubmit(handleCreateUser)}>
          <Stack gap="sm">
            <TextInput
              label={t("UserSettings.userForm.username")}
              withAsterisk
              disabled={creating}
              autoComplete="off"
              {...createForm.getInputProps("username")}
            />
            <PasswordInput
              label={t("UserSettings.userForm.temporaryPassword")}
              description={t("UserSettings.userForm.temporaryPasswordHint")}
              withAsterisk
              disabled={creating}
              autoComplete="new-password"
              {...createForm.getInputProps("password")}
            />
            <Select
              label={t("UserSettings.userForm.role")}
              description={t(`UserSettings.roleDescriptions.${createForm.values.role}`)}
              data={roleOptions}
              allowDeselect={false}
              withAsterisk
              disabled={creating}
              comboboxProps={{ zIndex: MODAL_POPOVER_Z_INDEX }}
              renderOption={({ option, checked }) => (
                <Tooltip
                  label={t(`UserSettings.roleDescriptions.${option.value}`)}
                  position="right"
                  withArrow
                  multiline
                  w={280}
                  zIndex={MODAL_POPOVER_Z_INDEX + 1}
                >
                  <Group flex="1" gap="xs" justify="space-between" wrap="nowrap">
                    <Text size="sm">{option.label}</Text>
                    {checked && <IconCheck size={14} />}
                  </Group>
                </Tooltip>
              )}
              {...createForm.getInputProps("role")}
            />
            <TextInput
              label={t("UserSettings.userForm.name")}
              disabled={creating}
              {...createForm.getInputProps("name")}
            />
            <TextInput
              type="email"
              label={t("UserSettings.userForm.email")}
              disabled={creating}
              {...createForm.getInputProps("email")}
            />
            {createError && (
              <Alert color="red" variant="light">
                {createError}
              </Alert>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="outline" onClick={closeCreateModal} disabled={creating}>
                {t("UserSettings.cancel")}
              </Button>
              <Button type="submit" loading={creating}>
                {t("UserSettings.userForm.submit")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={!!renameTarget}
        onClose={closeRenameModal}
        title={t("UserSettings.rename.title")}
        zIndex={MODAL_Z_INDEX}
        centered
      >
        <form onSubmit={renameForm.onSubmit(handleRenameSubmit)}>
          <Stack gap="sm">
            <Text size="sm">
              {t("UserSettings.rename.current", { username: renameTarget?.username ?? "" })}
            </Text>
            <TextInput
              label={t("UserSettings.rename.newUsername")}
              withAsterisk
              disabled={renaming}
              autoComplete="off"
              {...renameForm.getInputProps("username")}
            />
            <Checkbox
              label={t("UserSettings.rename.migrateOrganization")}
              description={t("UserSettings.rename.migrateOrganizationHint", {
                orgId: renameTargetOrgId,
              })}
              disabled={renaming}
              {...renameForm.getInputProps("migrateOrganization", { type: "checkbox" })}
            />
            {renameForm.values.migrateOrganization && (
              <Alert color="orange" variant="light">
                {t("UserSettings.rename.migrateWarning", {
                  orgId: renameTargetOrgId,
                  newOrgId: renameForm.values.username.trim(),
                })}
              </Alert>
            )}
            {renameFailure && (
              <Alert color="red" variant="light">
                {renameFailure}
              </Alert>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="outline" onClick={closeRenameModal} disabled={renaming}>
                {t("UserSettings.cancel")}
              </Button>
              <Button
                type="submit"
                loading={renaming}
                color={renameForm.values.migrateOrganization ? "red" : undefined}
              >
                {t(
                  renameForm.values.migrateOrganization
                    ? "UserSettings.rename.submitWithMigration"
                    : "UserSettings.rename.submit",
                )}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
      <Group justify="space-between" align="center" mb="lg">
        <Title order={3}>{t("UserSettings.users")}</Title>
        {canManageUsers && orgId && (
          <Button leftSection={<IconUserPlus size={16} />} onClick={openCreateModal}>
            {t("UserSettings.createUser")}
          </Button>
        )}
      </Group>
      {successMessage && (
        <Alert
          color="green"
          variant="light"
          mb="md"
          withCloseButton
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}
      <Table.ScrollContainer minWidth="100%" maxHeight="30rem">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("UserSettings.username")}</Table.Th>
              <Table.Th w={32} />
              <Table.Th>{t("UserSettings.name")}</Table.Th>
              <Table.Th>{t("UserSettings.email")}</Table.Th>
              <Table.Th>{t("UserSettings.role")}</Table.Th>
              <Table.Th>{t("UserSettings.lastLogin")}</Table.Th>
              <Table.Th>{t("UserSettings.creationDate")}</Table.Th>
              {showActions && <Table.Th>{t("UserSettings.actions")}</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody style={{ textWrap: "nowrap" }}>
            {users_?.map((user) => {
              const { _id, username, name, email, role, lastLogin, creationDate } = user;
              const licenseInfo = licenseInfoMap[username];
              const licenceStatus: LicenseStatus = licenseInfo
                ? licenceDisplayStatus(licenseInfo)
                : "none";
              const showIcon = licenseInfo && licenceStatus !== "none";
              const isCurrentUser = username === currentUsername;
              const isDeleting = deletingId === _id;

              return (
                <Table.Tr key={_id}>
                  <Table.Td>{username}</Table.Td>
                  <Table.Td w={32}>
                    {showIcon && (
                      <HoverCard width={280} position="right" withArrow shadow="md" openDelay={100}>
                        <HoverCard.Target>
                          <ActionIcon
                            size="sm"
                            variant="light"
                            color={
                              LICENSE_ICON_COLOR[licenceStatus as Exclude<LicenseStatus, "none">]
                            }
                            aria-label={t("UserSettings.offlineLicense")}
                          >
                            <IconDeviceDesktop size={14} />
                          </ActionIcon>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <Stack gap={4}>
                            <Text size="sm" fw={600} mb={4}>
                              {t("UserSettings.offlineLicense")}
                            </Text>
                            <Text size="xs">
                              {t("UserSettings.requestCount", {
                                count: licenseInfo.requestCount,
                              })}
                            </Text>
                            <Text size="xs">
                              {t("UserSettings.lastRequestDate")}:{" "}
                              {formatDate(licenseInfo.lastRequestDate)}
                            </Text>
                            <Text size="xs">
                              {t("UserSettings.licenseGenerationDate")}:{" "}
                              {formatDate(licenseInfo.licenseGenerationDate)}
                            </Text>
                            {licenseInfo.licenseExpiresAt ? (
                              <Text
                                size="xs"
                                c={licenceStatus === "expired" ? "red" : undefined}
                                fw={licenceStatus === "expired" ? 600 : undefined}
                              >
                                {t(
                                  licenceStatus === "expired"
                                    ? "UserSettings.licenseExpired"
                                    : "UserSettings.licenseExpiresAt",
                                )}
                                : {formatDate(licenseInfo.licenseExpiresAt)} (
                                {dayjs(licenseInfo.licenseExpiresAt).fromNow()})
                              </Text>
                            ) : (
                              licenseInfo.hasLicense && (
                                <Text size="xs" c="dimmed">
                                  {t("UserSettings.licenseExpiresAt")}:{" "}
                                  {t("UserSettings.licenseExpiryUnknown")}
                                </Text>
                              )
                            )}
                            <Text size="xs">
                              {t("UserSettings.lastSyncDate")}:{" "}
                              {formatDate(licenseInfo.lastSyncDate)}
                            </Text>
                          </Stack>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    )}
                  </Table.Td>
                  <Table.Td>{name}</Table.Td>
                  <Table.Td>{email}</Table.Td>
                  <Table.Td>{roleLabel(role)}</Table.Td>
                  <Table.Td>{dayjs(lastLogin).fromNow()}</Table.Td>
                  <Table.Td>{dayjs(creationDate).format("YYYY-MM-DD")}</Table.Td>
                  {showActions && (
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {canResetPasswords && (
                          <Tooltip
                            label={
                              isCurrentUser
                                ? t("UserSettings.resetPasswordSelfDisabled")
                                : t("UserSettings.resetPassword")
                            }
                          >
                            <ActionIcon
                              variant="light"
                              color="orange"
                              aria-label={
                                isCurrentUser
                                  ? t("UserSettings.resetPasswordSelfDisabled")
                                  : t("UserSettings.resetPassword")
                              }
                              disabled={isCurrentUser || isResetting}
                              loading={resettingUsername === username}
                              onClick={() => openResetModal(user)}
                            >
                              <IconKey size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {canReissueLicenses && licenseInfo?.hasLicense && (
                          <Tooltip label={t("UserSettings.reissueLicense")}>
                            <ActionIcon
                              variant="light"
                              color={licenceStatus === "expired" ? "red" : "blue"}
                              aria-label={t("UserSettings.reissueLicense")}
                              disabled={isReissuing}
                              loading={reissuingUsername === username}
                              onClick={() => openReissueModal(user)}
                            >
                              <IconRefresh size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {canManageUsers && (
                          <>
                            <Tooltip
                              label={
                                isCurrentUser
                                  ? t("UserSettings.rename.selfDisabled")
                                  : t("UserSettings.rename.title")
                              }
                            >
                              <ActionIcon
                                variant="light"
                                aria-label={
                                  isCurrentUser
                                    ? t("UserSettings.rename.selfDisabled")
                                    : t("UserSettings.rename.title")
                                }
                                disabled={isCurrentUser || isDeleting}
                                onClick={() => openRenameModal(user)}
                              >
                                <IconPencil size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                isCurrentUser
                                  ? t("UserSettings.deleteUserSelfDisabled")
                                  : t("UserSettings.deleteUser")
                              }
                            >
                              <ActionIcon
                                variant="light"
                                color="red"
                                aria-label={
                                  isCurrentUser
                                    ? t("UserSettings.deleteUserSelfDisabled")
                                    : t("UserSettings.deleteUser")
                                }
                                disabled={isCurrentUser || !!deletingId}
                                loading={isDeleting}
                                onClick={() => confirmDelete(user)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
            {!users_?.length && (
              <Table.Tr>
                <Table.Td colSpan={columnCount} align="center">
                  <Text mt="md">{t("UserSettings.noUsersFound")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}
